const cssClasses = {
    mailbox: 'js-mailbox',
    selectManyControl: 'js-select-many',
    selectNoneControl: 'js-select-none',
    sortDirControl: 'js-sort-dir',
    sortDirControlToggle: 'btn_outline',
    sortFieldControl: 'js-sort-fld',
    sortFieldControlToggle: 'active',
    selectable: 'js-selectable',
    selected: 'mailbox-selected',
    deleteButton: 'js-mailbox-delete',
    disabled: 'btn_disabled',
    innerTrigger: 'js-trigger-inner',
    innerTriggerTarget: 'js-trigger-inner-target',
};

const dataAttributes = {
    href: 'href',
    sortCommandArgs: 'sortCommand',
    target: 'target',
    triggerInner: 'triggerInner',
};

const selectors = {
    clickable: '.js-clickable[data-href]',
    suppressClick: '.js-suppress-click',
    sort: '.js-sort',
};

export default class MessageCenter {
    constructor() {
        document.addEventListener('DOMContentLoaded', this._init.bind(this));
    }

    get _sortState() {
        if (!this.__sortState) {
            let state = window.location.search.match(/sort=([a-z]*)\-([a-z]*)/i);

            if (!state) {
                state = []; // Create empty array.
            }

            state = {
                field: state[1] ? state[1] : 'date',
                direction: state[2] ? state[2] : 'desc',
            };
            this.__sortState = state;
        }
        return this.__sortState;
    }

    set _sortState(value) {
        const oldState = this._sortState;
        const newState = value;

        // Update field.
        newState.field = newState.field !== 'current' ? newState.field : oldState.field;
        // Update direction.
        if (newState.direction === 'toggle') {
            newState.direction = oldState.direction === 'asc' ? 'desc' : 'asc';
        } else {
            newState.direction = newState.direction !== 'current' ? newState.direction : oldState.direction;
        }

        newState.isDirty = oldState.isDirty ||
            newState.field !== oldState.field ||
            newState.direction !== oldState.direction;

        this.__sortState = newState;

        if (newState.isDirty && newState.invokeNow) {
            this._sort();
        }
    }

    _enableInnerTrigger(parent) {
        const elements = parent.getElementsByClassName(cssClasses.innerTrigger);

        if (!elements || !elements.length) { return; }

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];

            const config = element.dataset[dataAttributes.triggerInner].split(',');
            const eventName = config[0];
            const targetCssClass = config[1] ? config[1] : cssClasses.innerTriggerTarget;

            const targetElements = Array.from(element.getElementsByClassName(targetCssClass));
            element.addEventListener(eventName, (evt => {
                // Ignore propagating events.
                if (evt.target !== evt.currentTarget) { return; }

                targetElements.forEach(elem => {
                    const validTagNames = 'input,select,button';
                    if (elem.tagName !== validTagNames) {
                        /* eslint-disable no-param-reassign */
                        // Get the first child element with a valid tag name.
                        elem = elem.querySelector(validTagNames);
                        /* eslint-enable no-param-reassign */
                    }
                    const dispatchedEvent = new MouseEvent(eventName);
                    elem.dispatchEvent(dispatchedEvent);
                });
            }).bind(this));
        }
    }

    _enableSelectNone(parent) {
        const elements = parent.getElementsByClassName(cssClasses.selectNoneControl);
        if(!elements || !elements.length) { return; }

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            const targets = parent
                .querySelectorAll(`${element.dataset[dataAttributes.target]} > input[type="checkbox"]`);

            element.addEventListener('click', (() => {
                const selectAllTriggers = parent.getElementsByClassName(cssClasses.selectManyControl);
                for(let j = 0; j < selectAllTriggers.length; j++) {
                    const triggers = selectAllTriggers[j].querySelectorAll('input[type="checkbox"]');
                    for(let k = 0; k < triggers.length; k++) {
                        triggers[k].checked = false;
                    }
                }
                for(let j = 0; j < targets.length; j++) {
                    const target = targets[j];
                    target.checked = false;
                    this._fireSelectionChangedEvent(target);
                }
            }).bind(this));
        }
    }

    _enableSelectMany(parent) {
        const elements = parent.getElementsByClassName(cssClasses.selectManyControl);
        if (!elements || !elements.length) { return; }

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            /* ASP.NET wraps a checkbox in a <span> when you apply a `CssClass` to an 
                <asp:Checkbox> control. The `CssClass` is placed on that wrapping <span>. */
            const targets = parent
                .querySelectorAll(`${element.dataset[dataAttributes.target]} > input[type="checkbox"]`);
            element.addEventListener('click', (() => {
                const checked = element.checked;
                for (let j = 0; j < targets.length; j++) {
                    const target = targets[j];
                    target.checked = checked;
                    this._fireSelectionChangedEvent(target);
                }
            }).bind(this));
        }
    }

    _enableSelect(parent) {
        const elements = parent.getElementsByClassName(cssClasses.selectable);
        if(!elements || !elements.length) { return; }

        for(let i = 0; i < elements.length; i++) {
            const element = elements[i];
            const deleteButtons = parent.getElementsByClassName(cssClasses.deleteButton);
            const checkbox = element.querySelector('input[type="checkbox"]');
            
            if(checkbox.checked) {
                element.classList.add(cssClasses.selected);
                this._addSelectedItem(element);
                for(let j = 0; j < deleteButtons.length; j++) {
                    const button = deleteButtons[j]
                    button.classList.remove(cssClasses.disabled);
                    button.disabled = false;
                }
            }

            checkbox.addEventListener('change', (evt => {
                if(checkbox.checked) {
                    element.classList.add(cssClasses.selected);
                    this._addSelectedItem(element);

                    for(let j = 0; j < deleteButtons.length; j++) {
                        const button = deleteButtons[j];
                        button.classList.remove(cssClasses.disabled);
                        button.disabled = false;
                    }
                } else {
                    element.classList.remove(cssClasses.selected);
                    this._removeSelectedItem(element);

                    if(!this._anySelectedItems()) {
                        document.location.hash = '';
                        for(let j = 0; j < deleteButtons.length; j++) {
                            const button = deleteButtons[j];
                            button.classList.add(cssClasses.disabled);
                            button.disabled = true;
                        }
                    }
                }
            }).bind(this));
        }
    }

    _enableSorting(parent) {
        const elements = parent.querySelectorAll(selectors.sort);
        if (!elements || !elements.length) { return; }

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            element.addEventListener('click', (evt => {
                let args = evt.currentTarget.dataset[dataAttributes.sortCommandArgs];
                if (!args) { return; }

                args = args.split('-');
                args = { field: args[0], direction: args[1], invokeNow: args[2] };

                this._sortState = args;

                // Manage UI state of sort-direction buttons on mobile.
                if (evt.currentTarget.classList.contains(cssClasses.sortDirControl)) {
                    const buttons = evt.currentTarget.parentElement.getElementsByClassName(cssClasses.sortDirControl);
                    Array.from(buttons).forEach(element => {
                        if (element === evt.currentTarget) {
                            element.classList.remove(cssClasses.sortDirControlToggle);
                        } else {
                            element.classList.add(cssClasses.sortDirControlToggle);
                        }
                    });
                }
                // Manage UI state of sort-direction buttons on mobile.
                if (evt.currentTarget.classList.contains(cssClasses.sortFieldControl)) {
                    const buttons = evt.currentTarget.parentElement.getElementsByClassName(cssClasses.sortFieldControl);
                    Array.from(buttons).forEach(element => {
                        if (element === evt.currentTarget) {
                            element.classList.add(cssClasses.sortFieldControlToggle);
                        } else {
                            element.classList.remove(cssClasses.sortFieldControlToggle);
                        }
                    });
                }
            }).bind(this));
        }
    }

    _init() {
        const mailboxControls = document.getElementsByClassName(cssClasses.mailbox);

        if (!mailboxControls || !mailboxControls.length) {
            return;
        }

        for (let i = 0; i < mailboxControls.length; i++) {
            const element = mailboxControls[i];
            this._makeClickable(element);
            this._suppressClicks(element);
            this._enableSelect(element);
            this._enableSelectMany(element);
            this._enableSelectNone(element);
            this._enableSorting(element);
            this._enableInnerTrigger(element);
        }
    }

    _makeClickable(parent) {
        const elements = parent.querySelectorAll(selectors.clickable);

        if (!elements || !elements.length) { return; }

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            element.addEventListener('click', (evt => {
                window.location.href = evt.currentTarget.dataset[dataAttributes.href];
            }).bind(this));
        }
    }

    _sort() {
        const state = this.__sortState;
        console.log('COMMIT:', this.__sortState);

        const sortElementRegEx = /^sort/i;
        const newSortValue = `sort=${state.field}-${state.direction}`;

        let queryString = window.location.search.split('&');

        const index = queryString.findIndex(value => {
            return sortElementRegEx.test(value);
        });

        if (index === -1) {
            queryString.push(newSortValue);
        } else {
            queryString[index] = newSortValue;
        }
        queryString = queryString.join('&');
        window.location.hash = '';
        window.location.search = queryString;
    }

    _suppressClicks(parent) {
        const elements = parent.querySelectorAll(selectors.suppressClick);
        if (!elements || !elements.length) { return; }

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            element.addEventListener('click', (evt => {
                evt.stopPropagation();
            }).bind(this));
        }
    }

    _addSelectedItem(element) {
        if(!this.__checkedItems) {
            this.__checkedItems = [];
        }
        this.__checkedItems.push(element);
    }

    _removeSelectedItem(element) {
        if(!this.__checkedItems) { return; }

        const index = this.__checkedItems.indexOf(element);
        if(index > -1) {
            this.__checkedItems.splice(index, 1);
        }
    }

    _anySelectedItems() {
        return this.__checkedItems.length > 0;
    }
    
    _fireSelectionChangedEvent(element) {
        if("createEvent" in document) {
            var evt = document.createEvent("HTMLEvents");
            evt.initEvent('change', false, true);
            element.dispatchEvent(evt);
        } else {
            element.fireEvent('onchange');
        }
    }
}
